import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';
// Imported by path rather than from the barrel, because the tests here mock
// `@rsos/sinatra` wholesale and a barrel import would have to be added to that
// factory to avoid "not a function".
import { getTokens } from '@rsos/sinatra';
import { refreshAccessToken } from '@rsos/sinatra/src/utils/tokens';
import { getAPIHost } from '@rsos/utils/metaTags';

const STATUS_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
};

const baseURL = `${getAPIHost()}/v1/rem`;
const authURL = `${getAPIHost()}/v1/scorpius`;

// Extends the standard axios config with a custom field used to thread the
// retry flag through interceptors. Axios strips unknown AxiosRequestConfig
// fields automatically, so this is the only reliable way to pass it along.
interface MidasRequestConfig extends AxiosRequestConfig {
  custom?: { retry: boolean };
}

const opts: MidasRequestConfig = {
  baseURL,
  headers: {},
  withCredentials: true,
};

const initialTokens = getTokens();
if (initialTokens) {
  opts.headers = { Authorization: `Bearer ${initialTokens.token}` };
}

// TODO(eddie) - DRY this up with Sinatra's scorpiusInstance.js by creating
// a factory function that creates API instances that are authed via scorpius
const instance = axios.create(opts);

// When a request is sent with { headers: { retry: true } }, move the flag
// into config.custom and remove it from headers so axios doesn't forward it
// to the server.
instance.interceptors.request.use(config => {
  const midasConfig = config as MidasRequestConfig;
  const retry = midasConfig.headers?.retry;
  if (retry) {
    midasConfig.custom = { retry: true };
    delete midasConfig.headers?.retry;
  }
  return config;
});

// When a 401/403 arrives on a retryable request, refresh the access token
// and replay the original request with the new token.
instance.interceptors.response.use(
  undefined,
  async (error: AxiosError<unknown, MidasRequestConfig>) => {
    const retryCodes = [STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED];
    const config = error.config as MidasRequestConfig | undefined;

    const shouldRetry =
      config &&
      error.response &&
      (retryCodes as number[]).includes(error.response.status) &&
      config.custom?.retry;

    if (!shouldRetry) {
      return Promise.reject(error);
    }

    // Posted with the bare axios rather than `instance`, so a 401 on the
    // refresh itself cannot re-enter this interceptor.
    const refreshed = await refreshAccessToken({
      refreshToken: getTokens()?.refresh_token,
      transport: (path, body) => axios.post(`${authURL}/${path}`, body),
    });

    // Every failure — nothing to refresh with, a rejected refresh token, or a
    // 2xx carrying no token — rejects with the error from the original
    // request, because that is the failure the caller asked about. Replaying
    // the request without a new token would only send `Bearer undefined`.
    if (refreshed.status !== 'refreshed') {
      throw error;
    }

    instance.defaults.headers.Authorization = `Bearer ${refreshed.token}`;
    if (config.headers) {
      config.headers.Authorization = `Bearer ${refreshed.token}`;
    }

    return axios.request(config);
  },
);

export default instance;
